import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Pagination,
  Row,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import { FaCalendar, FaClock, FaUser } from "react-icons/fa";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import { getAuthUserlocal } from "../../../../../store/services";

const Meeting = () => {
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState({ upcoming: 1, ongoing: 1, past: 1 });
  const slotsPerPage = 6; // 6 meetings per page
  const userId = getAuthUserlocal()._id; // Collaborator’s ID

  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${getURLbyEndPointV2("getCollaboratorBookedSlots")}${userId}`
        );
        setBookedSlots(response.data.data);
      } catch (error) {
        console.error("Error fetching collaborator meetings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMeetings();
  }, [userId]);

  // Filter slots by status (using backend-provided status)
  const upcomingSlots = bookedSlots.filter(slot => slot.status === "upcoming");
  const ongoingSlots = bookedSlots.filter(slot => slot.status === "ongoing");
  const pastSlots = bookedSlots.filter(slot => slot.status === "past");

  // Pagination Logic for Each Tab
  const getPaginatedSlots = (slots, tab) => {
    const totalSlots = slots.length;
    const totalPages = Math.ceil(totalSlots / slotsPerPage);
    const currentPageNum = currentPage[tab];
    const paginatedSlots = slots.slice(
      (currentPageNum - 1) * slotsPerPage,
      currentPageNum * slotsPerPage
    );
    return { paginatedSlots, totalPages };
  };

  const handleJoinMeeting = (joinLink) => {
    window.open(joinLink, "_blank");
  };

  const handlePageChange = (tab, page) => {
    setCurrentPage(prev => ({ ...prev, [tab]: page }));
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (bookedSlots.length === 0) {
    return (
      <div className='p-2'>
        <h5>Meetings</h5>
        <hr />
        <Container className="d-flex justify-content-center align-items-center my-5 text-center">
          <div>
            <FaCalendar size={50} className="text-muted mb-3" />
            <h3 className="text-secondary">No Meetings Scheduled</h3>
            <p className="text-muted">No users have booked slots with you yet.</p>
          </div>
        </Container>
      </div>
    );
  }

  const renderSlots = (slots, tab) => {
    const { paginatedSlots, totalPages } = getPaginatedSlots(slots, tab);
    return (
      <>
        <Row>
          {paginatedSlots.map((slot) => (
            <Col key={slot._id} md={12} lg={6} className="mb-4">
              <Card className="meeting-card h-100">
                <Card.Header
                  className={`text-white ${tab === "upcoming" ? "bg-success" : tab === "ongoing" ? "bg-warning" : "bg-danger"}`}
                >
                  <h5 className="mb-0">{slot.country}</h5>
                  <small>{tab.charAt(0).toUpperCase() + tab.slice(1)}</small>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FaUser className="text-primary" />
                    <span>
                      <strong>company:</strong>{" "}
                      {slot.bookedBy?.companyName}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span>
                      <strong>Business:</strong>{" "}
                      {slot.bookedBy?.businessCategory}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span>
                      <strong>Business:</strong>{" "}
                      {slot.bookedBy?.firstName} {slot.bookedBy?.surName}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FaCalendar className="text-primary" />
                    <span>
                      <strong>Date:</strong>{" "}
                      {new Date(slot.date).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FaClock className="text-primary" />
                    <span>
                      <strong>Time:</strong> {slot.startTime} - {slot.endTime} (IST)
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span>
                      <strong>passcode:</strong> {slot.password}
                    </span>
                  </div>
                  <div className="text-muted mb-3">
                    <strong>Duration:</strong> {slot.duration} mins
                  </div>
                  <Button
                    variant="outline-primary"
                    className="w-100 mt-2"
                    onClick={() => handleJoinMeeting(slot.videoConferenceLink)}
                    disabled={tab === "past"}
                  >
                    {tab === "past" ? "Meeting Ended" : "Join Meeting"}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        {totalPages > 1 && (
          <Pagination className="justify-content-center mt-4">
            <Pagination.Prev
              onClick={() => handlePageChange(tab, currentPage[tab] - 1)}
              disabled={currentPage[tab] === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage[tab]}
                onClick={() => handlePageChange(tab, i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(tab, currentPage[tab] + 1)}
              disabled={currentPage[tab] === totalPages}
            />
          </Pagination>
        )}
        {paginatedSlots.length === 0 && (
          <p className="text-center text-muted">No {tab} meetings found.</p>
        )}
      </>
    );
  };

  return (
    <Container>
      <Tabs
        defaultActiveKey="upcoming"
        id="collaborator-meeting-tabs"
        className="mb-4"
        fill
        variant="pills"
      >
        <Tab eventKey="upcoming" title={`Upcoming (${upcomingSlots.length})`}>
          {renderSlots(upcomingSlots, "upcoming")}
        </Tab>
        <Tab eventKey="ongoing" title={`Ongoing (${ongoingSlots.length})`}>
          {renderSlots(ongoingSlots, "ongoing")}
        </Tab>
        <Tab eventKey="past" title={`Past (${pastSlots.length})`}>
          {renderSlots(pastSlots, "past")}
        </Tab>
      </Tabs>
    </Container>
  );
};

export default Meeting;